/**
 * UpgradeOptionsCard Component
 * Side-by-side plan comparison with upgrade CTAs
 *
 * Features:
 * - Show plans higher than current tier
 * - Monthly/annual toggle
 * - Preview modal before Stripe redirect
 * - Mobile-friendly (stacks vertically <768px)
 */

import { useState } from 'react'
import { Card, Row, Col, Button, Space, Typography, Tag, Radio } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useStripeCheckout } from '@/hooks/useStripeCheckout'
import { UpgradePreviewModal } from '@/components/billing/UpgradePreviewModal'
import { PLAN_TIERS } from '@/types/billing.types'
import type { PlanTier } from '@/types/billing.types'

const { Title, Text, Paragraph } = Typography

interface Props {
  currentTier: PlanTier
}

export function UpgradeOptionsCard({ currentTier }: Props) {
  const { createCheckout, loading } = useStripeCheckout()
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly')
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [selectedTier, setSelectedTier] = useState<PlanTier | null>(null)

  // Get tiers higher than current
  const tierOrder: PlanTier[] = ['free', 'growth', 'professional', 'business']
  const currentIndex = tierOrder.indexOf(currentTier)
  const availableTiers = tierOrder.slice(currentIndex + 1)

  const handleUpgradeClick = (tier: PlanTier) => {
    setSelectedTier(tier)
    setPreviewModalVisible(true)
  }

  const handleConfirmUpgrade = async () => {
    if (!selectedTier) return

    // TODO: Replace with actual Stripe Price IDs from environment/config
    const priceId = `price_${selectedTier}_${billingPeriod}`

    await createCheckout(priceId)
  }

  if (availableTiers.length === 0) {
    return null // User is on highest tier
  }

  return (
    <>
      <Card title="Upgrade Your Plan" id="upgrade-options">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Billing period toggle */}
          <div style={{ textAlign: 'center' }}>
            <Radio.Group
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="monthly">Monthly</Radio.Button>
              <Radio.Button value="annual">
                Annual <Tag color="green" style={{ marginLeft: '4px' }}>Save 20%</Tag>
              </Radio.Button>
            </Radio.Group>
          </div>

          {/* Plan comparison */}
          <Row gutter={[16, 16]}>
            {availableTiers.map(tier => {
              const plan = PLAN_TIERS[tier]
              const price = billingPeriod === 'annual' && plan.priceAnnual
                ? plan.priceAnnual
                : plan.priceMonthly
              const priceLabel = billingPeriod === 'annual' ? '/year' : '/month'

              return (
                <Col xs={24} sm={24} md={8} key={tier}>
                  <Card
                    style={{
                      border: plan.recommended ? '2px solid #1890ff' : undefined,
                      position: 'relative'
                    }}
                  >
                    {plan.recommended && (
                      <Tag
                        color="blue"
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px'
                        }}
                      >
                        Recommended
                      </Tag>
                    )}

                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {/* Plan name */}
                      <Title level={4} style={{ margin: 0 }}>
                        {plan.displayName}
                      </Title>

                      {/* Price */}
                      <div>
                        <Title level={2} style={{ margin: 0 }}>
                          ${price}
                        </Title>
                        <Text type="secondary">{priceLabel}</Text>
                      </div>

                      {/* Limits */}
                      <div>
                        <Paragraph style={{ margin: '4px 0' }}>
                          <Text strong>
                            {typeof plan.skuLimit === 'number'
                              ? plan.skuLimit.toLocaleString()
                              : plan.skuLimit} SKUs
                          </Text>
                        </Paragraph>
                        <Paragraph style={{ margin: '4px 0' }}>
                          <Text strong>
                            {plan.smartimportLimit === 'Unlimited'
                              ? 'Unlimited'
                              : plan.smartimportLimit} SmartImport runs
                          </Text>
                        </Paragraph>
                      </div>

                      {/* Features */}
                      <Space direction="vertical" size="small">
                        {plan.features.map((feature, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <CheckOutlined style={{ color: '#52c41a', marginTop: '4px', flexShrink: 0 }} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                      </Space>

                      {/* CTA button */}
                      <Button
                        type={plan.recommended ? 'primary' : 'default'}
                        size="large"
                        block
                        onClick={() => handleUpgradeClick(tier)}
                      >
                        Select {plan.displayName}
                      </Button>
                    </Space>
                  </Card>
                </Col>
              )
            })}
          </Row>

          {/* Annual savings tip */}
          {billingPeriod === 'monthly' && (
            <Paragraph type="secondary" style={{ textAlign: 'center', margin: '8px 0 0 0' }}>
              Tip: Annual billing saves 20% (2 months free)
            </Paragraph>
          )}
        </Space>
      </Card>

      {/* Preview modal */}
      {selectedTier && (
        <UpgradePreviewModal
          visible={previewModalVisible}
          currentTier={currentTier}
          newTier={selectedTier}
          billingPeriod={billingPeriod}
          onConfirm={handleConfirmUpgrade}
          onCancel={() => setPreviewModalVisible(false)}
          loading={loading}
        />
      )}
    </>
  )
}
